import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Merge, AlertTriangle, Loader2 } from "lucide-react";

export const MERGEABLE = ["aguardando", "aprovado", "iniciando"];
const STATUS_LABEL = { aguardando: "Aguardando", aprovado: "Aprovado", iniciando: "Iniciado" };

const Rules = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900 space-y-1" data-testid="merge-rules">
    <p className="font-black flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Condições</p>
    <p>• Só entre orçamentos da <b>mesma escola</b>, nos status <b>Aguardando, Aprovado ou Iniciado</b>.</p>
    <p>• Não é possível unir com orçamentos Finalizados, com Nota gerada ou Nota paga.</p>
    <p>• Os itens selecionados são <b>movidos</b> com todos os dados (fotos, conclusão, data, alterações).</p>
    <p>• Se mover <b>todos</b> os itens, este orçamento é <b>apagado</b> e o histórico dele vai para o destino.</p>
  </div>
);

const TargetList = ({ targets, value, onChange }) => (
  <div>
    <p className="text-xs font-bold text-slate-600 mb-1">Orçamento de destino <span className="font-normal text-slate-500">(toque para escolher)</span></p>
    {targets === null ? (
      <p className="text-xs text-slate-500 flex items-center"><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Carregando…</p>
    ) : targets.length === 0 ? (
      <p className="text-xs text-slate-500 italic" data-testid="merge-no-targets">Nenhum outro orçamento desta escola em Aguardando, Aprovado ou Iniciado.</p>
    ) : (
      <ul className="space-y-1.5 max-h-44 overflow-y-auto">
        {targets.map((t) => (
          <li key={t.id}>
            <button type="button" onClick={() => onChange(t.id)} data-testid={`merge-target-${t.number}`}
              className={`w-full text-left rounded-lg border-2 px-3 py-2 text-xs transition-colors flex items-start gap-2 ${value === t.id ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
              <span className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${value === t.id ? "border-sky-600" : "border-slate-300"}`}>
                {value === t.id && <span className="w-2 h-2 rounded-full bg-sky-600" />}
              </span>
              <span className="min-w-0 flex-1">
              <span className="font-black text-slate-900">Nº {t.number}</span> • {STATUS_LABEL[t.status] || t.status} • {BRL(t.total || 0)}
              {t.budget_date && <span className="text-slate-500"> • {formatDateBR(t.budget_date)}</span>}
              {t.service_type && <span className="block text-[10.5px] text-slate-500 truncate">{t.service_type}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function MergeBudgetDialog({ open, onOpenChange, budget }) {
  const nav = useNavigate();
  const [targets, setTargets] = useState(null);
  const [target, setTarget] = useState("");
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  const items = budget?.items || [];

  useEffect(() => {
    if (!open || !budget) return;
    setTargets(null); setTarget(""); setSel({});
    api.get(`/budgets/${budget.id}/merge-targets`).then((r) => {
      const list = r.data || [];
      setTargets(list);
      if (list.length === 1) setTarget(list[0].id);
    }).catch(() => setTargets([]));
  }, [open, budget]);

  const ids = Object.keys(sel).filter((k) => sel[k]);
  const all = ids.length === items.length && items.length > 0;
  const tNum = (targets || []).find((t) => t.id === target)?.number;

  const confirm = async () => {
    if (!target) return toast.error("Escolha o orçamento de destino");
    if (!ids.length) return toast.error("Selecione pelo menos 1 item");
    if (all && !window.confirm(`Todos os itens serão movidos para o Nº ${tNum} e este orçamento (Nº ${budget.number}) será APAGADO. Confirmar?`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/budgets/${budget.id}/merge`, { target_id: target, item_ids: ids });
      toast.success(`${r.data.moved_items} item(ns) movido(s) para o Nº ${r.data.target_number}${r.data.source_deleted ? " — origem apagada" : ""}`);
      onOpenChange(false);
      nav(`/orcamentos/${r.data.target_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao unir orçamentos");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5 text-sky-600" /> Unir orçamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Rules />
          <TargetList targets={targets} value={target} onChange={setTarget} />
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold text-slate-600">Itens para mover</p>
              <button type="button" className="text-[11px] font-bold text-sky-700 underline" data-testid="merge-select-all"
                onClick={() => setSel(all ? {} : Object.fromEntries(items.map((i) => [i.id, true])))}>
                {all ? "Limpar" : "Selecionar todos"}
              </button>
            </div>
            <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto border border-slate-100 rounded-xl">
              {items.map((it, idx) => (
                <li key={it.id} className="flex items-start gap-3 p-3">
                  <Checkbox checked={!!sel[it.id]} onCheckedChange={() => setSel((s) => ({ ...s, [it.id]: !s[it.id] }))} data-testid={`merge-item-${idx}`} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 line-clamp-2">{it.description || "(sem descrição)"}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Qtd {it.quantity || 1} × {BRL(it.unit_value || 0)} = <b>{BRL(it.total || 0)}</b>{it.completed ? " • concluído" : ""}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {all && (
            <p className="text-[11.5px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="merge-delete-warning">
              Todos os itens selecionados: este orçamento (Nº {budget?.number}) será apagado depois de unir.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={confirm} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="btn-confirm-merge">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
            {all ? "Unir e apagar origem" : `Mover ${ids.length || ""} item(ns)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
